import { useId } from 'react';
import { cn } from './cn.js';
import { Switch as SwitchPrimitive } from './primitives/switch.js';

/**
 * O interruptor de uma escolha binária com nome nos dois lados — não
 * "ligado/desligado" genérico: cada posição tem a própria palavra, como
 * `permitir`/`negar` no modal de exceções individuais de permissão.
 *
 * A mecânica (foco, teclado, `role="switch"`) vem do primitivo do shadcn; a
 * marca é o rótulo dos dois lados, sempre visível, e o lado ativo em negrito e
 * na cor do estado — nunca só a posição do traço, que é a mesma regra de
 * acessibilidade do `Badge`: estado é cor + texto, nunca cor sozinha.
 */
export function Switch({
  checked,
  onCheckedChange,
  onLabel,
  offLabel,
  label,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** O rótulo do lado LIGADO (`checked = true`), ex.: "Permitir". */
  onLabel: string;
  /** O rótulo do lado DESLIGADO (`checked = false`), ex.: "Negar". */
  offLabel: string;
  /** O nome do campo para quem usa leitor de tela — os dois rótulos ao lado são visuais. */
  label: string;
  disabled?: boolean;
}) {
  const id = useId();

  return (
    <span className="inline-flex items-center gap-2.5">
      <span
        className={cn(
          'text-sm font-medium transition-colors',
          checked ? 'text-texto-suave' : 'text-erro',
        )}
      >
        {offLabel}
      </span>
      <SwitchPrimitive
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-label={label}
        id={id}
        className={cn(
          'data-[state=checked]:bg-sucesso data-[state=unchecked]:bg-erro',
          'focus-visible:border-primaria-texto focus-visible:ring-primaria-texto/50',
        )}
      />
      <span
        className={cn(
          'text-sm font-medium transition-colors',
          checked ? 'text-sucesso' : 'text-texto-suave',
        )}
      >
        {onLabel}
      </span>
    </span>
  );
}
